import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft, Plus, ChefHat, Clock, Users as UsersIcon, Search, 
  Filter, MessageSquare, Heart, X, Trash2, Edit, Star
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { formatLocalDate } from '../utils/dateUtils';

const CommunityRecipesPage = () => {
  const navigate = useNavigate();
  const { user, token, API } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDietType, setSelectedDietType] = useState('all');
  const [deleteRecipeId, setDeleteRecipeId] = useState(null);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'breakfast', label: 'Breakfast' },
    { value: 'lunch', label: 'Lunch' },
    { value: 'dinner', label: 'Dinner' },
    { value: 'snack', label: 'Snacks' },
  ];

  const dietTypes = [
    { value: 'all', label: 'All Diet Types' },
    { value: 'low-histamine', label: 'Low-Histamine' },
    { value: 'anti-inflammatory', label: 'Anti-Inflammatory' },
    { value: 'autoimmune', label: 'Autoimmune Protocol' },
    { value: 'MCAS-friendly', label: 'MCAS-Friendly' },
    { value: 'gut-healing', label: 'Gut Healing' },
  ];

  const [newRecipe, setNewRecipe] = useState({
    title: '',
    description: '',
    category: 'dinner',
    diet_type: ['low-histamine'],
    prep_time: 15,
    cook_time: 30,
    servings: 4,
    ingredients: [''],
    instructions: [''],
    tips: '',
    is_kid_approved: false
  });

  useEffect(() => {
    fetchRecipes();
  }, [selectedCategory, selectedDietType, searchQuery]);

  const fetchRecipes = async () => {
    try {
      setLoading(true);
      let url = `${API}/community-recipes?`;
      if (selectedCategory !== 'all') url += `category=${selectedCategory}&`;
      if (selectedDietType !== 'all') url += `diet_type=${selectedDietType}&`;
      if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}&`;
      
      const response = await axios.get(url);
      setRecipes(response.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
      toast.error('Failed to load recipes');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (recipeId) => {
    try {
      const response = await axios.get(`${API}/community-recipes/${recipeId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleSubmitRecipe = async () => {
    if (!newRecipe.title.trim()) {
      toast.error('Please enter a recipe title');
      return;
    }
    if (newRecipe.ingredients.filter(i => i.trim()).length === 0) {
      toast.error('Please add at least one ingredient');
      return;
    }
    if (newRecipe.instructions.filter(i => i.trim()).length === 0) {
      toast.error('Please add at least one instruction');
      return;
    }

    try {
      const recipeData = {
        ...newRecipe,
        ingredients: newRecipe.ingredients.filter(i => i.trim()),
        instructions: newRecipe.instructions.filter(i => i.trim())
      };

      await axios.post(`${API}/community-recipes`, recipeData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Recipe shared with the community!');
      setShowAddForm(false);
      setNewRecipe({
        title: '',
        description: '',
        category: 'dinner',
        diet_type: ['low-histamine'],
        prep_time: 15,
        cook_time: 30,
        servings: 4,
        ingredients: [''],
        instructions: [''],
        tips: '',
        is_kid_approved: false
      });
      fetchRecipes();
    } catch (error) {
      console.error('Error creating recipe:', error);
      toast.error('Failed to share recipe');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await axios.post(
        `${API}/community-recipes/${selectedRecipe.id}/comments`,
        { content: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Comment added!');
      setNewComment('');
      fetchComments(selectedRecipe.id);
      // Update comment count locally
      setSelectedRecipe(prev => ({ ...prev, comment_count: prev.comment_count + 1 }));
      setRecipes(prev => prev.map(r => 
        r.id === selectedRecipe.id ? { ...r, comment_count: r.comment_count + 1 } : r
      ));
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteRecipe = async () => {
    if (!deleteRecipeId) return;

    try {
      await axios.delete(`${API}/community-recipes/${deleteRecipeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Recipe deleted');
      setDeleteRecipeId(null);
      if (selectedRecipe?.id === deleteRecipeId) {
        setSelectedRecipe(null);
      }
      fetchRecipes();
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error('Failed to delete recipe');
    }
  };

  const addIngredient = () => {
    setNewRecipe(prev => ({ ...prev, ingredients: [...prev.ingredients, ''] }));
  };

  const updateIngredient = (index, value) => {
    setNewRecipe(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => i === index ? value : ing)
    }));
  };

  const removeIngredient = (index) => {
    setNewRecipe(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const addInstruction = () => {
    setNewRecipe(prev => ({ ...prev, instructions: [...prev.instructions, ''] }));
  };

  const updateInstruction = (index, value) => {
    setNewRecipe(prev => ({
      ...prev,
      instructions: prev.instructions.map((inst, i) => i === index ? value : inst)
    }));
  };

  const removeInstruction = (index) => {
    setNewRecipe(prev => ({
      ...prev,
      instructions: prev.instructions.filter((_, i) => i !== index)
    }));
  };

  const toggleDietType = (type) => {
    setNewRecipe(prev => ({
      ...prev,
      diet_type: prev.diet_type.includes(type)
        ? prev.diet_type.filter(t => t !== type)
        : [...prev.diet_type, type]
    }));
  };

  const openRecipeDetail = (recipe) => {
    setSelectedRecipe(recipe);
    fetchComments(recipe.id);
  };

  return (
    <div className="space-y-6" data-testid="community-recipes-page">
      <Button 
        onClick={() => navigate('/recipes')} 
        variant="ghost" 
        className="text-primary hover:text-primary/80"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Recipes
      </Button>

      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <ChefHat className="w-10 h-10 text-primary" strokeWidth={1.5} />
              <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">
                Community Recipes
              </h1>
            </div>
            <p className="text-[#57534E]">
              Discover and share autoimmune-friendly recipes with other families
            </p>
          </div>
          <Button 
            onClick={() => setShowAddForm(true)}
            className="bg-primary text-white hover:bg-primary/90 rounded-full"
            data-testid="share-recipe-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Share a Recipe
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
            <Input
              type="text"
              placeholder="Search recipes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-full"
              data-testid="search-input"
            />
          </div>
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 rounded-full border border-stone-200 bg-white text-[#57534E] focus:ring-2 focus:ring-primary focus:border-primary"
          data-testid="category-filter"
        >
          {categories.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        <select
          value={selectedDietType}
          onChange={(e) => setSelectedDietType(e.target.value)}
          className="px-4 py-2 rounded-full border border-stone-200 bg-white text-[#57534E] focus:ring-2 focus:ring-primary focus:border-primary"
          data-testid="diet-filter"
        >
          {dietTypes.map(diet => (
            <option key={diet.value} value={diet.value}>{diet.label}</option>
          ))}
        </select>
      </div>

      {/* Recipe Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-[#57534E] mt-4">Loading recipes...</p>
        </div>
      ) : recipes.length === 0 ? (
        <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
          <ChefHat className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
          <h3 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">No recipes yet</h3>
          <p className="text-[#57534E] mb-4">Be the first to share a recipe with the community!</p>
          <Button 
            onClick={() => setShowAddForm(true)}
            className="bg-primary text-white hover:bg-primary/90 rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Share a Recipe
          </Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recipes.map((recipe) => (
            <Card 
              key={recipe.id}
              className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openRecipeDetail(recipe)}
              data-testid={`recipe-card-${recipe.id}`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs font-medium px-3 py-1 bg-secondary/20 text-secondary-foreground rounded-full capitalize">
                    {recipe.category}
                  </span>
                  {recipe.is_kid_approved && (
                    <span className="text-xs font-medium px-2 py-1 bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" /> Kid Approved
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-2">{recipe.title}</h3>
                <p className="text-sm text-[#57534E] line-clamp-2 mb-4">{recipe.description}</p>
                
                <div className="flex items-center gap-4 text-sm text-[#A8A29E] mb-4">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {recipe.prep_time + recipe.cook_time} min
                  </span>
                  <span className="flex items-center gap-1">
                    <UsersIcon className="w-4 h-4" />
                    {recipe.servings} servings
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" />
                    {recipe.comment_count}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1">
                  {recipe.diet_type.slice(0, 2).map((type, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 bg-stone-100 text-[#57534E] rounded-full">
                      {type}
                    </span>
                  ))}
                  {recipe.diet_type.length > 2 && (
                    <span className="text-xs px-2 py-0.5 bg-stone-100 text-[#57534E] rounded-full">
                      +{recipe.diet_type.length - 2}
                    </span>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between">
                  <span className="text-xs text-[#A8A29E]">by {recipe.user_name}</span>
                  {recipe.user_id === user?.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteRecipeId(recipe.id);
                      }}
                      className="p-1.5 text-[#A8A29E] hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Recipe Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">Share Your Recipe</h2>
              <button onClick={() => setShowAddForm(false)} className="p-2 hover:bg-stone-100 rounded-full">
                <X className="w-5 h-5 text-[#57534E]" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Recipe Title *</label>
                <Input
                  value={newRecipe.title}
                  onChange={(e) => setNewRecipe(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Low-Histamine Chicken Stir Fry"
                  className="rounded-lg"
                  data-testid="recipe-title-input"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Description</label>
                <textarea
                  value={newRecipe.description}
                  onChange={(e) => setNewRecipe(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Briefly describe your recipe..."
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                  rows={3}
                />
              </div>

              {/* Category & Times */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Category</label>
                  <select
                    value={newRecipe.category}
                    onChange={(e) => setNewRecipe(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-primary"
                  >
                    <option value="breakfast">Breakfast</option>
                    <option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option>
                    <option value="snack">Snack</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Prep Time (min)</label>
                  <Input
                    type="number"
                    value={newRecipe.prep_time}
                    onChange={(e) => setNewRecipe(prev => ({ ...prev, prep_time: parseInt(e.target.value) || 0 }))}
                    className="rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Cook Time (min)</label>
                  <Input
                    type="number"
                    value={newRecipe.cook_time}
                    onChange={(e) => setNewRecipe(prev => ({ ...prev, cook_time: parseInt(e.target.value) || 0 }))}
                    className="rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Servings</label>
                  <Input
                    type="number"
                    value={newRecipe.servings}
                    onChange={(e) => setNewRecipe(prev => ({ ...prev, servings: parseInt(e.target.value) || 1 }))}
                    className="rounded-lg"
                  />
                </div>
              </div>

              {/* Diet Types */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Diet Types</label>
                <div className="flex flex-wrap gap-2">
                  {['low-histamine', 'anti-inflammatory', 'autoimmune', 'MCAS-friendly', 'gut-healing'].map(type => (
                    <button
                      key={type}
                      onClick={() => toggleDietType(type)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        newRecipe.diet_type.includes(type)
                          ? 'bg-primary text-white'
                          : 'bg-stone-100 text-[#57534E] hover:bg-stone-200'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Ingredients */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Ingredients *</label>
                <div className="space-y-2">
                  {newRecipe.ingredients.map((ing, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={ing}
                        onChange={(e) => updateIngredient(index, e.target.value)}
                        placeholder="e.g., 2 cups diced chicken breast"
                        className="rounded-lg flex-1"
                      />
                      {newRecipe.ingredients.length > 1 && (
                        <button
                          onClick={() => removeIngredient(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <Button onClick={addIngredient} variant="outline" size="sm" className="rounded-full">
                    <Plus className="w-4 h-4 mr-1" /> Add Ingredient
                  </Button>
                </div>
              </div>

              {/* Instructions */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Instructions *</label>
                <div className="space-y-2">
                  {newRecipe.instructions.map((inst, index) => (
                    <div key={index} className="flex gap-2">
                      <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold mt-2">
                        {index + 1}
                      </span>
                      <textarea
                        value={inst}
                        onChange={(e) => updateInstruction(index, e.target.value)}
                        placeholder="Describe this step..."
                        className="flex-1 px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-primary resize-none"
                        rows={2}
                      />
                      {newRecipe.instructions.length > 1 && (
                        <button
                          onClick={() => removeInstruction(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg self-start"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <Button onClick={addInstruction} variant="outline" size="sm" className="rounded-full">
                    <Plus className="w-4 h-4 mr-1" /> Add Step
                  </Button>
                </div>
              </div>

              {/* Tips */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Tips & Notes (Optional)</label>
                <textarea
                  value={newRecipe.tips}
                  onChange={(e) => setNewRecipe(prev => ({ ...prev, tips: e.target.value }))}
                  placeholder="Any helpful tips for making this recipe..."
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-primary resize-none"
                  rows={2}
                />
              </div>

              {/* Kid Approved */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newRecipe.is_kid_approved}
                  onChange={(e) => setNewRecipe(prev => ({ ...prev, is_kid_approved: e.target.checked }))}
                  className="w-5 h-5 rounded border-stone-300 text-primary focus:ring-primary"
                />
                <span className="text-[#2D2D2D] font-medium">⭐ Kid Approved - Kids actually like this!</span>
              </label>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSubmitRecipe}
                  className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-full"
                  data-testid="submit-recipe-btn"
                >
                  Share Recipe
                </Button>
                <Button
                  onClick={() => setShowAddForm(false)}
                  variant="outline"
                  className="rounded-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Recipe Detail Modal */}
      {selectedRecipe && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium px-3 py-1 bg-secondary/20 text-secondary-foreground rounded-full capitalize">
                    {selectedRecipe.category}
                  </span>
                  {selectedRecipe.is_kid_approved && (
                    <span className="text-xs font-medium px-2 py-1 bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" /> Kid Approved
                    </span>
                  )}
                </div>
                <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">{selectedRecipe.title}</h2>
                <p className="text-sm text-[#A8A29E]">by {selectedRecipe.user_name}</p>
              </div>
              <button onClick={() => setSelectedRecipe(null)} className="p-2 hover:bg-stone-100 rounded-full">
                <X className="w-5 h-5 text-[#57534E]" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Description & Meta */}
              <div>
                <p className="text-[#57534E] leading-relaxed">{selectedRecipe.description}</p>
                <div className="flex flex-wrap gap-4 mt-4 text-sm text-[#57534E]">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4 text-primary" />
                    Prep: {selectedRecipe.prep_time} min
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4 text-primary" />
                    Cook: {selectedRecipe.cook_time} min
                  </span>
                  <span className="flex items-center gap-1">
                    <UsersIcon className="w-4 h-4 text-primary" />
                    {selectedRecipe.servings} servings
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {selectedRecipe.diet_type.map((type, i) => (
                    <span key={i} className="text-xs px-3 py-1 bg-primary/10 text-primary rounded-full">
                      {type}
                    </span>
                  ))}
                </div>
              </div>

              {/* Ingredients */}
              <div>
                <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-3">Ingredients</h3>
                <ul className="space-y-2">
                  {selectedRecipe.ingredients.map((ing, i) => (
                    <li key={i} className="flex items-start gap-2 text-[#57534E]">
                      <span className="text-primary">•</span>
                      <span>{ing}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Instructions */}
              <div>
                <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-3">Instructions</h3>
                <ol className="space-y-4">
                  {selectedRecipe.instructions.map((inst, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {i + 1}
                      </span>
                      <p className="text-[#57534E] pt-0.5">{inst}</p>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Tips */}
              {selectedRecipe.tips && (
                <div className="bg-amber-50 rounded-lg p-4">
                  <h3 className="font-semibold text-amber-800 mb-2">💡 Tips</h3>
                  <p className="text-amber-900 text-sm">{selectedRecipe.tips}</p>
                </div>
              )}

              {/* Comments Section */}
              <div className="border-t border-stone-200 pt-6">
                <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-4">
                  Comments ({selectedRecipe.comment_count})
                </h3>
                
                {/* Add Comment */}
                <div className="flex gap-3 mb-6">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="rounded-full"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  />
                  <Button 
                    onClick={handleAddComment}
                    className="bg-primary text-white hover:bg-primary/90 rounded-full"
                    disabled={!newComment.trim()}
                  >
                    Post
                  </Button>
                </div>

                {/* Comments List */}
                {comments.length === 0 ? (
                  <p className="text-[#A8A29E] text-center py-4">No comments yet. Be the first!</p>
                ) : (
                  <div className="space-y-4">
                    {comments.map((comment) => (
                      <div key={comment.id} className="bg-stone-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-[#2D2D2D]">{comment.user_name}</span>
                          <span className="text-xs text-[#A8A29E]">
                            {formatLocalDate(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-[#57534E] text-sm">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteRecipeId} onOpenChange={() => setDeleteRecipeId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recipe?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your recipe and all its comments. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRecipe}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CommunityRecipesPage;
